// Multi-manager buyer teams (WS4) — the roster.
//
// 🔴 THE ONE THING THIS TAB HAS TO SAY OUT LOUD. `activeBuyers` counts what
// each PERSON is carrying across every team, because capacity follows the
// person (D2) while lead counts follow the lead. So the column does NOT sum to
// the team's lead count and is not supposed to; a reader who assumes it does
// will read a healthy roster as a broken one. That is a caption on the card,
// not a tooltip — a rule you have to hover to discover is a rule most readers
// never see.

import { useState } from "react";
import { Plus } from "lucide-react";
import { extractErrorMessage } from "@/api/client";
import type { BuyerTeamDetailDto, BuyerTeamMemberDto, BuyerTeamRole } from "@/api/buyerTeams";
import { RoleFilteredUserPicker } from "@/components/DealFlow/common/RoleFilteredUserPicker";
import { Modal } from "@/components/ui/Modal";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { Select } from "@/components/ui/Select";
import {
  Button,
  Card,
  StatusBadge,
  SubTable,
  Timestamp,
  useToast,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { useAddBuyerTeamMember, useRemoveBuyerTeamMember } from "@/hooks/buyers/useBuyerTeams";

/**
 * 🔴 The whole assignable roster, un-narrowed — the same reasoning the team's
 * manager picker carries. The server validates that the user EXISTS and takes
 * no view on their role, so a role-derived shortlist here would be a second
 * eligibility rule free to disagree with the enforced one, and it would fail
 * silently: a person the server would accept simply would not be in the list.
 */
const ANY_ASSIGNABLE_USER = "";

const ROLE_OPTIONS: BuyerTeamRole[] = ["Specialist", "Manager"];

/**
 * What a person reads for each team role. The VALUE is a wire value the server
 * stores and validates (`BuyerTeamRoles`), so it never changes; only the words
 * shown for it do. "Specialist" is the F2 seat, whose title is Buyer Consultant.
 * Every place a role is SHOWN reads through this — the option text, the roster
 * column, the toast and the confirm — and every place it is SENT uses the value.
 */
const ROLE_IN_TEAM_LABEL: Record<BuyerTeamRole, string> = {
  Specialist: "Consultant",
  Manager: "Manager",
};

type Pending =
  | { kind: "add" }
  | { kind: "confirm-add"; userId: string; roleInTeam: BuyerTeamRole }
  | { kind: "remove"; member: BuyerTeamMemberDto }
  | null;

export function MembersTab({
  team,
  canManage,
}: {
  team: BuyerTeamDetailDto;
  canManage: boolean;
}) {
  const toast = useToast();
  const [pending, setPending] = useState<Pending>(null);

  const add = useAddBuyerTeamMember(team.id);
  const remove = useRemoveBuyerTeamMember(team.id);

  const archived = team.status === "Archived";

  function confirmAdd() {
    if (pending?.kind !== "confirm-add") return;
    const { userId, roleInTeam } = pending;
    add.mutate(
      { userId, roleInTeam },
      {
        onSuccess: () => {
          setPending(null);
          toast.success(`Added to "${team.name}" as ${ROLE_IN_TEAM_LABEL[roleInTeam].toLowerCase()}.`);
        },
        onError: (e) => {
          setPending(null);
          toast.error(extractErrorMessage(e));
        },
      },
    );
  }

  function confirmRemove() {
    if (pending?.kind !== "remove") return;
    const { member } = pending;
    const who = member.displayName ?? member.userId;
    remove.mutate(member.userId, {
      onSuccess: () => {
        setPending(null);
        toast.success(`${who} removed from "${team.name}".`);
      },
      onError: (e) => {
        setPending(null);
        toast.error(extractErrorMessage(e));
      },
    });
  }

  const columns: SubTableColumn<BuyerTeamMemberDto>[] = [
    {
      key: "member",
      header: "Member",
      sortAccessor: (m) => m.displayName ?? m.userId,
      render: (m) => (
        <div className="min-w-0">
          <div className="font-semibold text-onyx truncate">{m.displayName ?? m.userId}</div>
          {m.email ? (
            <div className="text-caption text-gray-mid truncate">{m.email}</div>
          ) : null}
        </div>
      ),
      searchText: (m) => `${m.displayName ?? ""} ${m.email ?? ""} ${m.userId}`,
    },
    {
      key: "role",
      header: "Role",
      sortAccessor: (m) => m.roleInTeam,
      // Manager is the exception on a roster of specialists, so it is the one
      // that carries a pill; the ordinary seat reads as prose.
      render: (m) =>
        m.roleInTeam === "Manager" ? (
          <StatusBadge tone="progress">Manager</StatusBadge>
        ) : (
          <span className="text-small text-gray-mid">{ROLE_IN_TEAM_LABEL[m.roleInTeam]}</span>
        ),
    },
    {
      key: "joined",
      header: "Joined",
      sortAccessor: (m) => m.joinedAtUtc,
      render: (m) => <Timestamp value={m.joinedAtUtc} absolute className="text-gray-mid" />,
    },
    {
      key: "activeBuyers",
      header: "Buyers held",
      align: "right",
      sortAccessor: (m) => m.activeBuyers,
      render: (m) => <span className="tabular-nums">{m.activeBuyers}</span>,
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: "",
            searchText: () => "",
            render: (m: BuyerTeamMemberDto) => (
              <Button
                size="sm"
                variant="text"
                onClick={() => setPending({ kind: "remove", member: m })}
                data-testid={`buyer-team-member-remove-${m.userId}`}
              >
                Remove
              </Button>
            ),
          } satisfies SubTableColumn<BuyerTeamMemberDto>,
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-space-4" data-testid="buyer-team-members-tab">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-space-3 mb-space-3">
          <div className="min-w-0">
            <h2 className="font-display text-h3 text-onyx m-0">Roster</h2>
            {/* The D2 rule, stated where the number it governs is read. */}
            <p className="text-caption text-gray-mid m-0 mt-space-1 max-w-reading">
              <strong>Buyers held</strong> counts what each person is carrying across every
              team, not this team&apos;s share of it — capacity follows the person. It is not
              expected to equal this team&apos;s {team.leadCount} lead
              {team.leadCount === 1 ? "" : "s"}.
            </p>
          </div>
          {canManage && (
            <Button
              size="sm"
              onClick={() => setPending({ kind: "add" })}
              disabled={archived}
              data-testid="buyer-team-add-member"
            >
              <Plus size={14} strokeWidth={1.5} aria-hidden /> Add member
            </Button>
          )}
        </div>

        {canManage && archived && (
          <p className="text-caption text-gray-mid m-0 mb-space-3" role="note">
            This team is archived, so it takes no new members. Unarchive it first.
          </p>
        )}

        <SubTable<BuyerTeamMemberDto>
          caption={`Members of ${team.name}`}
          data-testid="buyer-team-roster"
          rows={team.members}
          rowKey={(m) => m.userId}
          columns={columns}
          searchPlaceholder="Search members…"
          empty={{
            message: "Nobody is on this team yet.",
            door: canManage
              ? {
                  kind: "stamp",
                  label: "Add member",
                  onClick: () => setPending({ kind: "add" }),
                  disabled: archived,
                  disabledReason: "Archived teams take no new members.",
                }
              : {
                  kind: "derived",
                  explain: "Members are added by someone who manages the buyer org structure.",
                },
          }}
        />
      </Card>

      {pending?.kind === "add" && (
        <AddMemberModal
          teamName={team.name}
          onClose={() => setPending(null)}
          onPicked={(userId, roleInTeam) => setPending({ kind: "confirm-add", userId, roleInTeam })}
        />
      )}

      {pending?.kind === "confirm-add" && (
        <ConfirmModal
          open
          title="Add to this team?"
          message={
            <>
              <p className="m-0 mb-space-2">
                Add them to <strong>{team.name}</strong> as {ROLE_IN_TEAM_LABEL[pending.roleInTeam].toLowerCase()}.
              </p>
              {/* The side effect on a record the reader is NOT looking at. */}
              <p className="m-0">
                A person is on exactly one buyer team. If they are already on another team,
                this <strong>moves</strong> them — that team loses them from its roster.
              </p>
            </>
          }
          confirmLabel="Add to team"
          onConfirm={confirmAdd}
          onCancel={() => setPending(null)}
        />
      )}

      {pending?.kind === "remove" && (
        <ConfirmModal
          open
          title="Remove from team?"
          message={
            <>
              <p className="m-0 mb-space-2">
                Remove <strong>{pending.member.displayName ?? pending.member.userId}</strong>{" "}
                from {team.name}?
              </p>
              <p className="m-0">
                The buyers they hold stay with them — this changes the roster, not who owns a
                lead. They end up on no team until they are added to one.
              </p>
            </>
          }
          confirmLabel="Remove"
          destructive
          onConfirm={confirmRemove}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}

function AddMemberModal({
  teamName,
  onClose,
  onPicked,
}: {
  teamName: string;
  onClose: () => void;
  onPicked: (userId: string, roleInTeam: BuyerTeamRole) => void;
}) {
  const [userId, setUserId] = useState<string | null>(null);
  const [roleInTeam, setRoleInTeam] = useState<BuyerTeamRole>("Specialist");

  return (
    <Modal
      open
      onClose={onClose}
      title="Add member"
      description={`Put a staff user on ${teamName}.`}
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!userId}
            onClick={() => userId && onPicked(userId, roleInTeam)}
            data-testid="buyer-team-add-member-continue"
          >
            Continue
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-3" data-testid="buyer-team-add-member-modal">
        <div className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid uppercase tracking-label">Staff user</span>
          <RoleFilteredUserPicker
            role={ANY_ASSIGNABLE_USER}
            value={userId}
            onChange={setUserId}
            placeholder="Select a user…"
            aria-label="Staff user"
          />
        </div>
        <label className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid uppercase tracking-label">Role on team</span>
          <Select
            value={roleInTeam}
            onChange={(e) => setRoleInTeam(e.target.value as BuyerTeamRole)}
            aria-label="Role on team"
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {ROLE_IN_TEAM_LABEL[r]}
              </option>
            ))}
          </Select>
        </label>
      </div>
    </Modal>
  );
}
